import { useCallback, useEffect, useState } from "react"
import type { Currency, PaginatedResponse, Pair } from "../types"
import { formatDate, getStatusColor } from "../lib/utils"
import { apiClient } from "../lib/api"
import { Pagination } from "../components/Pagination"

export function CurrencyControlsPage() {
  // Modal states
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false)

  // Message states
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Common data states
  const [currencies, setCurrencies] = useState<PaginatedResponse<Currency>>({
    data: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 1 },
  })
  const [pairs, setPairs] = useState<PaginatedResponse<Pair>>({
    data: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 1 },
  })

  //   Form states
  const [addCurrencyForm, setAddCurrencyForm] = useState<{
    name: string
    symbol: string
    currencyType: "FIAT" | "CRYPTO"
  }>({
    name: "",
    symbol: "",
    currencyType: "" as "FIAT" | "CRYPTO",
  })

  const loadCurrencies = useCallback(() => {
    apiClient
      .listCurrencies()
      .then((data) => {
        setCurrencies(data)
      })
      .catch(() => {})
  }, [])

  const loadPairs = useCallback((page = 1, limit = 10) => {
    apiClient
      .listPairs({ page, limit })
      .then((data) => {
        setPairs(data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadCurrencies()
    loadPairs()
  }, [loadCurrencies, loadPairs])

  const handleAddCurrency = async () => {
    try {
      if (!addCurrencyForm || !addCurrencyForm.name || !addCurrencyForm.symbol || !addCurrencyForm.currencyType) {
        throw new Error("Please fill all the required fields")
      }
      const currency = await apiClient.addCurrency(addCurrencyForm)
      setSuccessMessage(`${currency.currencyType} currency ${currency.symbol} added successfully!`)
      setShowAddCurrencyModal(false)
      setAddCurrencyForm({ name: "", symbol: "", currencyType: "FIAT" })
      loadCurrencies()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to add currency")
    }
  }

  const handleTogglePairStatus = async (pairId: string, newStatus: boolean) => {
    try {
      await apiClient.updatePair(pairId, { enabled: newStatus })
      setPairs((prev) => ({
        ...prev,
        data: prev.data.map((pair) => (pair.id === pairId ? { ...pair, enabled: newStatus } : pair)),
      }))
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update pair status")
    }
  }

  const handleToggleCurrencyStatus = async (currencyId: string, newStatus: boolean) => {
    try {
      await apiClient.updateCurrency(currencyId, { enabled: newStatus })
      setCurrencies((prev) => ({
        ...prev,
        data: prev.data.map((currency) =>
          currency.id === currencyId ? { ...currency, enabled: newStatus } : currency,
        ),
      }))
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update currency status")
    }
  }

  return (
    <div style={{ padding: "2rem" }}>
      {/* Success Message */}
      {successMessage && (
        <div
          style={{
            padding: "1rem",
            marginBottom: "1rem",
            background: "rgba(34, 197, 94, 0.1)",
            border: "1px solid rgba(34, 197, 94, 0.3)",
            borderRadius: "8px",
            color: "#86efac",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            style={{
              background: "none",
              border: "none",
              color: "#86efac",
              cursor: "pointer",
              fontSize: "1.25rem",
              padding: "0 0.5rem",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Currencies */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "1.5rem 0rem", alignItems: "center" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>
            Currencies ({currencies.pagination.total})
          </h2>
          <button
            type="button"
            onClick={() => setShowAddCurrencyModal(true)}
            style={{
              padding: "0.5rem 1rem",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              border: "none",
              borderRadius: "8px",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Add Currency
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Symbol</th>
                <th>Status</th>
                <th colSpan={2}>Created</th>
              </tr>
            </thead>
            <tbody>
              {currencies.data.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#94a3b8" }}>
                    No data exists
                  </td>
                </tr>
              ) : (
                currencies.data.map((currency) => (
                  <tr key={currency.id}>
                    <td>{currency.name}</td>
                    <td>{currency.symbol}</td>
                    <td>
                      <span className={`badge badge-${getStatusColor(currency.enabled ? "active" : "default")}`}>
                        {currency.enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{formatDate(currency.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          handleToggleCurrencyStatus(currency.id, !currency.enabled)
                        }}
                        style={{
                          padding: "0.25rem 0.5rem",
                          background: currency.enabled ? "#f87171" : "#34d399",
                          border: "none",
                          borderRadius: "4px",
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {currency.enabled ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {currencies.pagination.total > 0 && (
              <Pagination pagination={currencies.pagination} onChange={loadCurrencies} />
            )}
          </table>
        </div>
      </div>

      {/* Pairs */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "1.5rem 0rem", alignItems: "center" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Pairs ({pairs.pagination.total})</h2>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Pair</th>
                <th>Status</th>
                <th colSpan={2}>Created</th>
              </tr>
            </thead>
            <tbody>
              {pairs.data.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#94a3b8" }}>
                    No data exists
                  </td>
                </tr>
              ) : (
                pairs.data.map((pair) => (
                  <tr key={pair.id}>
                    <td>{pair.pair}</td>
                    <td>
                      <span className={`badge badge-${getStatusColor(pair.enabled ? "active" : "default")}`}>
                        {pair.enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{formatDate(pair.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          handleTogglePairStatus(pair.id, !pair.enabled)
                        }}
                        style={{
                          padding: "0.25rem 0.5rem",
                          background: pair.enabled ? "#f87171" : "#34d399",
                          border: "none",
                          borderRadius: "4px",
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {pair.enabled ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {pairs.pagination.total > 0 && <Pagination pagination={pairs.pagination} onChange={loadPairs} />}
          </table>
        </div>
      </div>

      {/* Add Currency Modal */}
      {showAddCurrencyModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#1e293b",
              borderRadius: "12px",
              padding: "2rem",
              width: "35%",
              maxWidth: "75%",
              maxHeight: "75%",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1.5rem" }}>
              Add Currency
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label
                  htmlFor="currency-name"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Name *
                </label>
                <input
                  id="currency-name"
                  type="text"
                  value={addCurrencyForm.name}
                  onChange={(e) => setAddCurrencyForm({ ...addCurrencyForm, name: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="currency-symbol"
                  style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}
                >
                  Symbol *
                </label>
                <input
                  id="currency-symbol"
                  type="text"
                  value={addCurrencyForm.symbol}
                  onChange={(e) => setAddCurrencyForm({ ...addCurrencyForm, symbol: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                    color: "#e2e8f0",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="currency-type"
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#94a3b8",
                  }}
                >
                  Select Currency
                </label>
                <select
                  id="currency-type"
                  value={addCurrencyForm.currencyType}
                  onChange={(e) =>
                    setAddCurrencyForm({ ...addCurrencyForm, currencyType: e.target.value as "FIAT" | "CRYPTO" })
                  }
                  style={{
                    width: "100%",
                  }}
                >
                  <option value="">Select currency type</option>
                  <option value="CRYPTO">CRYPTO</option>
                  <option value="FIAT">FIAT</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddCurrencyModal(false)
                  setAddCurrencyForm({ name: "", symbol: "", currencyType: "FIAT" })
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCurrency}
                disabled={!addCurrencyForm.name || !addCurrencyForm.symbol || !addCurrencyForm.currencyType}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  background:
                    !addCurrencyForm.name || !addCurrencyForm.symbol || !addCurrencyForm.currencyType
                      ? "rgba(99, 102, 241, 0.3)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor:
                    !addCurrencyForm.name || !addCurrencyForm.symbol || !addCurrencyForm.currencyType
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
